import { useEffect, useMemo, useRef, useState, Children } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Evidenzia il termine cercato dentro una stringa di testo del Markdown.
function highlight(text, term) {
    if (!term || typeof text !== "string") return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
    return parts.map((p, i) =>
        p.toLowerCase() === term.toLowerCase() ? (
            <mark
                key={i}
                className="bg-tff7a1a/35 text-tffffff rounded px-0.5"
                data-reg-match="1"
            >
                {p}
            </mark>
        ) : (
            p
        ),
    );
}

const mapChildren = (children, term) =>
    Children.map(children, (c) => (typeof c === "string" ? highlight(c, term) : c));

// Renderer Markdown con evidenziazione del termine sui nodi di testo.
function makeComponents(term) {
    const wrap = (Tag) =>
        function Comp({ node, children, ...props }) {
            return <Tag {...props}>{mapChildren(children, term)}</Tag>;
        };
    return {
        p: wrap("p"),
        li: wrap("li"),
        strong: wrap("strong"),
        em: wrap("em"),
        a: wrap("a"),
        td: wrap("td"),
        th: wrap("th"),
        h1: wrap("h1"),
        h2: wrap("h2"),
        h3: wrap("h3"),
        h4: wrap("h4"),
    };
}

const countMatches = (a, term) => {
    if (!term) return 0;
    const re = new RegExp(escapeRegExp(term), "ig");
    return ((a.title + " " + (a.content || "")).match(re) || []).length;
};

export default function Regulation() {
    const [articles, setArticles] = useState([]);
    const [active, setActive] = useState(null);
    const [search, setSearch] = useState("");
    const [params, setParams] = useSearchParams();
    const bodyRef = useRef(null);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/regulation");
            setArticles(data || []);
            const reqId = params.get("article");
            const initial = (data || []).find((a) => a.id === reqId) || data?.[0] || null;
            setActive(initial);
        })();
        // eslint-disable-next-line
    }, []);

    const onSelect = (a) => {
        setActive(a);
        setParams({ article: a.id }, { replace: true });
    };

    const term = search.trim();

    const filtered = useMemo(
        () =>
            articles.filter(
                (a) =>
                    !term ||
                    a.title.toLowerCase().includes(term.toLowerCase()) ||
                    a.number.includes(term) ||
                    (a.section || "").toLowerCase().includes(term.toLowerCase()) ||
                    (a.content || "").toLowerCase().includes(term.toLowerCase()),
            ),
        [articles, term],
    );

    // Quando cerchi, se l'articolo attivo non è tra i risultati, salta al primo che contiene la regola.
    useEffect(() => {
        if (!term) return;
        if (filtered.length && !filtered.some((a) => a.id === active?.id)) {
            setActive(filtered[0]);
        }
        // eslint-disable-next-line
    }, [term, filtered]);

    // Scrolla al primo match evidenziato nel corpo dell'articolo.
    useEffect(() => {
        if (!term || !bodyRef.current) return;
        const t = setTimeout(() => {
            const mark = bodyRef.current?.querySelector('[data-reg-match="1"]');
            if (mark) mark.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 120);
        return () => clearTimeout(t);
    }, [term, active]);

    const components = useMemo(() => makeComponents(term), [term]);

    // group by section
    const grouped = filtered.reduce((acc, a) => {
        const sec = a.section || "Generale";
        if (!acc[sec]) acc[sec] = [];
        acc[sec].push(a);
        return acc;
    }, {});

    const totalMatches = term
        ? filtered.reduce((s, a) => s + countMatches(a, term), 0)
        : 0;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="regulation-page">
            <div className="op-label text-t2bb6ff">// REGOLAMENTO UFFICIALE</div>
            <h1 className="op-heading text-5xl sm:text-6xl mt-3 mb-2">
                ORIGINAL PES <span className="op-gradient-text">RULEBOOK</span>
            </h1>
            <p className="text-t8f95a3 max-w-2xl text-sm leading-relaxed">
                La fonte ufficiale per la gestione dell'universo OP. Può essere aggiornata
                previa approvazione del CDP. Fa fede esclusivamente la versione vigente.
            </p>

            <div className="grid lg:grid-cols-[300px_1fr] gap-6 mt-10">
                {/* Sidebar index */}
                <aside className="op-card p-4 self-start lg:sticky lg:top-20" data-testid="regulation-sidebar">
                    <div className="relative mb-2">
                        <input
                            type="text"
                            className="op-input pr-8"
                            placeholder="Cerca una regola…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="regulation-search-input"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-t8f95a3 hover:text-tffffff text-lg leading-none"
                                title="Pulisci"
                                data-testid="regulation-search-clear"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {term && (
                        <div className="op-mono text-[11px] text-t8f95a3 mb-3" data-testid="regulation-search-count">
                            {totalMatches > 0
                                ? `${totalMatches} risultati in ${filtered.length} articol${filtered.length === 1 ? "o" : "i"}`
                                : "Nessun risultato"}
                        </div>
                    )}
                    <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
                        {Object.entries(grouped).map(([section, items]) => (
                            <div key={section}>
                                <div className="op-label mb-2">{section}</div>
                                <ul className="space-y-1">
                                    {items.map((a) => {
                                        const mc = countMatches(a, term);
                                        return (
                                            <li key={a.id}>
                                                <button
                                                    onClick={() => onSelect(a)}
                                                    className={`w-full text-left px-3 py-2 text-sm transition-colors duration-150 border-l-2 flex items-center justify-between gap-2 ${
                                                        active?.id === a.id
                                                            ? "border-t2bb6ff bg-t2bb6ff/5 text-tffffff"
                                                            : "border-transparent text-tc9ccd3 hover:border-t22252a hover:bg-t1a1d24"
                                                    }`}
                                                    data-testid={`regulation-article-link-${a.number}`}
                                                >
                                                    <span className="truncate">
                                                        <span className="op-mono text-t8f95a3 mr-2">
                                                            {a.number}
                                                        </span>
                                                        {a.title}
                                                    </span>
                                                    {term && mc > 0 && (
                                                        <span className="op-mono text-[10px] shrink-0 bg-tff7a1a/20 text-tff7a1a px-1.5 py-0.5 rounded">
                                                            {mc}
                                                        </span>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                        {filtered.length === 0 && (
                            <div className="text-t5c6370 text-sm">Nessun articolo trovato.</div>
                        )}
                    </div>
                </aside>

                {/* Article body */}
                <article className="op-card p-8" data-testid="regulation-article-body" ref={bodyRef}>
                    {active ? (
                        <>
                            <div className="flex items-center gap-3 mb-3">
                                <span className="op-mono text-t2bb6ff text-sm border border-t2bb6ff/40 px-2 py-1">
                                    ART. {active.number}
                                </span>
                                {active.section && (
                                    <span className="op-label">{active.section}</span>
                                )}
                            </div>
                            <h2 className="op-heading text-4xl sm:text-5xl mb-6">
                                {highlight(active.title, term)}
                            </h2>
                            <div className="op-prose">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                                    {active.content}
                                </ReactMarkdown>
                            </div>
                        </>
                    ) : (
                        <div className="op-label">Nessun articolo selezionato.</div>
                    )}
                </article>
            </div>
        </div>
    );
}
